// CSS-only premium hero mockup composition: laptop + phone + analytics card.
// Avoids stock photos and AI-generated faces; gives a clean, brand-aligned visual.
import { TrendingUp, Activity } from "lucide-react";

export const HeroMockup = () => {
    return (
        <div className="relative w-full max-w-[560px] mx-auto aspect-[5/4] select-none" data-testid="hero-mockup">
            {/* Glow */}
            <div className="absolute -inset-10 bg-[#14B8A6]/15 blur-3xl rounded-full" aria-hidden />

            {/* Laptop */}
            <div className="absolute left-0 top-2 w-[88%] z-10">
                <div className="rounded-t-2xl border border-white/10 bg-[#0F172A] shadow-2xl shadow-black/60 overflow-hidden">
                    <div className="h-6 bg-[#0B1220] flex items-center gap-1.5 px-3 border-b border-white/5">
                        <span className="w-2 h-2 rounded-full bg-white/20" />
                        <span className="w-2 h-2 rounded-full bg-white/20" />
                        <span className="w-2 h-2 rounded-full bg-white/20" />
                    </div>
                    <div className="aspect-[16/9] p-4 md:p-5 bg-gradient-to-br from-[#0F172A] to-[#06111F]">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1.5">
                                <span className="w-5 h-5 rounded-md bg-[#14B8A6] flex items-center justify-center text-[10px] font-bold text-[#06111F]">SA</span>
                                <span className="text-white text-[10px] font-semibold tracking-wider">SZYMKAS</span>
                            </div>
                            <div className="flex gap-3 text-[8px] text-white/40">
                                <span>Usługi</span><span>Pakiety</span><span>Kontakt</span>
                            </div>
                        </div>
                        <div className="h-1.5 w-2/3 bg-white/15 rounded-full mb-2" />
                        <div className="h-1.5 w-1/2 bg-[#14B8A6] rounded-full mb-3" />
                        <div className="h-1 w-3/4 bg-white/10 rounded mb-1" />
                        <div className="h-1 w-2/3 bg-white/10 rounded mb-3" />
                        <div className="grid grid-cols-3 gap-1.5">
                            <div className="aspect-square rounded-md bg-white/5 border border-white/10" />
                            <div className="aspect-square rounded-md bg-[#14B8A6]/20 border border-[#14B8A6]/40" />
                            <div className="aspect-square rounded-md bg-white/5 border border-white/10" />
                        </div>
                    </div>
                </div>
                <div className="h-2 bg-[#0B1220] rounded-b-2xl border-x border-b border-white/10" />
                <div className="h-1 w-1/3 mx-auto bg-[#0F172A] rounded-b-md" />
            </div>

            {/* Phone */}
            <div className="absolute right-0 bottom-0 w-[34%] z-20">
                <div className="rounded-[22px] border-2 border-white/15 bg-[#0F172A] shadow-2xl shadow-black/60 p-1.5">
                    <div className="rounded-[16px] bg-gradient-to-b from-[#06111F] to-[#0F172A] aspect-[9/19] overflow-hidden p-3">
                        <div className="flex items-center justify-between mb-3">
                            <span className="w-4 h-4 rounded-full bg-[#14B8A6]" />
                            <span className="text-[7px] text-white/40">Instagram</span>
                            <span className="w-3 h-3 rounded-sm bg-white/10" />
                        </div>
                        <div className="flex items-center gap-1.5 mb-3">
                            <span className="w-7 h-7 rounded-full bg-[#14B8A6]/30 border border-[#14B8A6]" />
                            <div className="flex-1">
                                <div className="h-1 w-2/3 bg-white/30 rounded mb-1" />
                                <div className="h-0.5 w-1/2 bg-white/15 rounded" />
                            </div>
                        </div>
                        <div className="aspect-square rounded-md bg-gradient-to-br from-[#14B8A6]/40 to-[#0EA5A5]/20 mb-2 border border-[#14B8A6]/30" />
                        <div className="grid grid-cols-3 gap-1">
                            <div className="aspect-square rounded bg-white/5" />
                            <div className="aspect-square rounded bg-[#14B8A6]/20" />
                            <div className="aspect-square rounded bg-white/5" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Analytics card */}
            <div className="absolute left-2 bottom-4 w-[42%] z-30">
                <div className="rounded-xl bg-white/95 backdrop-blur p-3 md:p-4 shadow-2xl shadow-black/60 border border-white/20">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-semibold text-[#0B1220]">Wizyty (30 dni)</span>
                        <Activity className="w-3 h-3 text-[#14B8A6]" />
                    </div>
                    <div className="text-xl md:text-2xl font-bold text-[#0B1220] font-display">+148%</div>
                    <div className="flex items-end gap-1 mt-2 h-8">
                        {[30, 45, 38, 55, 60, 72, 68, 88, 95].map((h, i) => (
                            <span
                                key={i}
                                className={`flex-1 rounded-sm ${i > 5 ? "bg-[#14B8A6]" : "bg-[#14B8A6]/30"}`}
                                style={{ height: `${h}%` }}
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-[#14B8A6] text-[9px] font-semibold">
                        <TrendingUp className="w-2.5 h-2.5" /> rosnący trend
                    </div>
                </div>
            </div>

            {/* Logo badge */}
            <div className="absolute -top-2 right-6 z-30 hidden md:block">
                <div className="rounded-lg bg-[#14B8A6] text-[#06111F] text-[10px] font-bold px-2.5 py-1 shadow-lg">
                    SA Branding
                </div>
            </div>
        </div>
    );
};
